import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Browser-side Web Push subscription helper.
 *
 * Flow:
 *   1. `GET /api/notifications/webpush/key`        — fetch the VAPID
 *      public key the backend was configured with.
 *   2. `navigator.serviceWorker.ready`             — must be controlled
 *      by our SW for push to reach.
 *   3. `pushManager.getSubscription()`             — reuse if present.
 *   4. `Notification.requestPermission()`          — only call this from
 *      a user gesture (the button click). Browsers reject silent prompts.
 *   5. `pushManager.subscribe({ userVisibleOnly, applicationServerKey })`
 *   6. `POST /api/notifications/webpush/register`  — persist server-side.
 *
 * Errors are returned as `{ ok: false, reason }` so the caller can
 * render a meaningful toast.
 */

export async function isSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export async function currentPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function subscribeWebPush(token) {
  if (!(await isSupported())) {
    return { ok: false, reason: 'unsupported' };
  }

  // 1. Fetch VAPID public key.
  let publicKey;
  try {
    const res = await axios.get(`${API}/notifications/webpush/key`);
    publicKey = res.data?.public_key;
  } catch {
    return { ok: false, reason: 'key_fetch_failed' };
  }
  if (!publicKey) {
    return { ok: false, reason: 'webpush_not_configured' };
  }

  // 2. SW must be active.
  const reg = await navigator.serviceWorker.ready;
  if (!reg) return { ok: false, reason: 'no_service_worker' };

  // 3. Reuse existing subscription if present.
  let sub = await reg.pushManager.getSubscription();

  if (!sub) {
    // 4. Permission — caller should already have prompted.
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return { ok: false, reason: 'permission_denied' };

    // 5. Subscribe.
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (err) {
      return { ok: false, reason: `subscribe_failed:${err.message || err}` };
    }
  }

  // 6. Register with backend.
  try {
    await axios.post(
      `${API}/notifications/webpush/register`,
      { subscription: sub.toJSON() },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return { ok: true, endpoint: sub.endpoint };
  } catch (err) {
    return { ok: false, reason: `register_failed:${err.response?.data?.detail || err.message}` };
  }
}

export async function unsubscribeWebPush() {
  if (!(await isSupported())) return { ok: true };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) await sub.unsubscribe();
  return { ok: true };
}

// VAPID keys are base64url-encoded; Web Crypto wants a Uint8Array.
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
